import { RadioGroup } from 'radix-ui';
import { cn } from '../../lib/cn';
import { useControlled } from '../../lib/use-controlled';
import { Icon, type LucideIcon } from './icon';

export interface SegmentItem {
  value: string;
  label?: string;
  icon?: LucideIcon;
  count?: number;
}

export interface SegmentedControlProps {
  items: SegmentItem[];
  /** `null`: gesteuert, nichts gewählt (z. B. „Alle“, wenn die Einträge darunter verschieden stehen). */
  value?: string | null;
  defaultValue?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  /** Name der Gruppe für Screenreader. */
  label?: string;
  className?: string;
}

/**
 * Schaltet zwischen Ansichten derselben Daten um (Liste, Board, Karte). Zwei bis fünf
 * Einträge. Auf Touch-Geräten 44 px je Segment (README „Mobil“, Ziel 44 px). Ohne Text werden die Icons zur Leiste; dann ist `value` der Name für Screenreader.
 */
export function SegmentedControl({ items, value, defaultValue, onChange, disabled, label, className }: SegmentedControlProps) {
  const [cur, set] = useControlled<string | null>(value, defaultValue ?? items[0]?.value ?? '', (v) => v !== null && onChange?.(v));
  const nurIcons = items.every((i) => !i.label);
  return (
    <RadioGroup.Root
      // '' trifft keinen Eintrag: nichts gewählt.
      value={cur ?? ''}
      onValueChange={set}
      disabled={disabled}
      aria-label={label}
      orientation="horizontal"
      className={cn('inline-flex gap-0.5 rounded-md border border-border bg-muted p-[3px]', className)}
    >
      {items.map((it) => (
        <RadioGroup.Item
          key={it.value}
          value={it.value}
          aria-label={it.label ? undefined : it.value}
          title={it.label ? undefined : it.value}
          className={cn(
            'inline-flex h-7 touch:h-11 cursor-pointer items-center disabled:cursor-not-allowed disabled:opacity-45 gap-1.5 rounded-sm text-[13px] font-medium leading-[18px] text-muted-foreground transition-colors duration-[120ms] hover:text-foreground',
            'data-[state=checked]:bg-popover data-[state=checked]:text-foreground data-[state=checked]:shadow-sm [&[data-state=checked]>svg]:text-highlight',
            nurIcons ? 'px-[9px]' : 'px-3',
          )}
        >
          {it.icon && <Icon icon={it.icon} />}
          {it.label}
          {it.count != null && <span className="font-mono text-[11px] text-muted-foreground">{it.count}</span>}
        </RadioGroup.Item>
      ))}
    </RadioGroup.Root>
  );
}
